import { useCallback, useEffect, useState } from 'react';
import { formatDateTime } from '@nu/shared';
import { api } from '../../api/client.js';
import { problem } from './util.js';

// Platform > Booking types (admins): what can be booked, as utility, client
// and work type. Edit in place; a type that's been used can only be switched
// off, so its old bookings keep their label.

const UTILITIES = [
  { value: 'water', label: 'Water' },
  { value: 'gas', label: 'Gas' },
  { value: 'electricity', label: 'Electricity' },
  { value: 'other', label: 'Other' },
];
const EMPTY = { utility: 'water', client: '', workType: '' };

export default function BookingTypes() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(EMPTY);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api('/booking-types'));
    } catch (e) {
      setError(problem(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (fn, ok) => {
    setBusy(true);
    setNotice(null);
    try {
      const r = await fn();
      if (r?.types) setData(r);
      else await load();
      setNotice({ tone: 'ok', text: ok });
      return true;
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
      await load();
      return false;
    } finally {
      setBusy(false);
    }
  };
  const update = (t, body, ok) => save(() => api(`/booking-types/${t.id}`, { method: 'PUT', body }), ok);
  const add = async (e) => {
    e.preventDefault();
    const ok = await save(() => api('/booking-types', { method: 'POST', body: draft }), `${draft.client.trim()} · ${draft.workType.trim()} added.`);
    if (ok) setDraft(EMPTY);
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Booking types</h1>
            <p className="muted">What can be booked: the utility, the client and the work type. Click a name to change it. A type that has bookings can&apos;t be deleted, only switched off, so its bookings keep their label.</p>
          </div>
        </div>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
      </section>

      <section className="card">
        {error && <div className="empty"><p>{error}</p><button type="button" className="btn" onClick={load}>Try again</button></div>}
        {!error && !data && <p className="muted">Loading…</p>}
        {data && (
          <div className="table-scroll">
            <table className="table roster booking-types">
              <thead><tr><th>Utility</th><th>Client</th><th>Work type</th><th>Bookings</th><th>In use</th></tr></thead>
              <tbody>
                {data.types.map((t) => (
                  <tr key={t.id} className={t.active ? '' : 'inactive'}>
                    <td data-label="Utility">
                      <select value={t.utility} disabled={busy} aria-label={`Utility for ${t.client} ${t.workType}`}
                        onChange={(e) => update(t, { utility: e.target.value }, `${t.client} · ${t.workType} is now ${e.target.value}.`)}>
                        {UTILITIES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                    </td>
                    <td data-label="Client">
                      <Inline value={t.client} label={`Client for ${t.workType}`} disabled={busy}
                        onSave={(v) => update(t, { client: v }, `Client renamed to ${v}.`)} />
                    </td>
                    <td data-label="Work type">
                      <Inline value={t.workType} label={`Work type for ${t.client}`} disabled={busy}
                        onSave={(v) => update(t, { workType: v }, `Work type renamed to ${v}.`)} />
                    </td>
                    <td data-label="Bookings">{t.bookings}</td>
                    <td data-label="In use">
                      <input type="checkbox" role="switch" className="switch" checked={t.active} disabled={busy} aria-label={`${t.client} ${t.workType} in use`}
                        onChange={(e) => update(t, { active: e.target.checked }, `${t.client} · ${t.workType} ${e.target.checked ? 'is back in use' : 'switched off'}.`)} />
                    </td>
                  </tr>
                ))}
                <tr className="add-row">
                  <td data-label="Utility">
                    <select form="add-type" value={draft.utility} aria-label="Utility for the new type" onChange={(e) => setDraft((d) => ({ ...d, utility: e.target.value }))}>
                      {UTILITIES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </td>
                  <td data-label="Client">
                    <input className="cell-input wide" form="add-type" placeholder="e.g. Network Plus" maxLength={80} value={draft.client} aria-label="Client for the new type"
                      onChange={(e) => setDraft((d) => ({ ...d, client: e.target.value }))} />
                  </td>
                  <td data-label="Work type">
                    <input className="cell-input wide" form="add-type" placeholder="e.g. G Install" maxLength={80} value={draft.workType} aria-label="Work type for the new type"
                      onChange={(e) => setDraft((d) => ({ ...d, workType: e.target.value }))} />
                  </td>
                  <td colSpan={2}>
                    <form id="add-type" onSubmit={add}>
                      <button type="submit" className="btn primary" disabled={busy || !draft.client.trim() || !draft.workType.trim()}>Add type</button>
                    </form>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      {data?.history?.length > 0 && (
        <section className="card">
          <h2 className="section-title">History</h2>
          <ul className="history">
            {data.history.map((h) => <li key={h.id}><p>{h.summary}</p><span className="small muted">{h.actorEmail} · {formatDateTime(h.changedAt)}</span></li>)}
          </ul>
        </section>
      )}
    </div>
  );
}

// Saves on Enter or leaving the box, only if it changed; Escape puts it back.
function Inline({ value, onSave, label, disabled }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const commit = () => {
    const t = v.replace(/\s+/g, ' ').trim();
    if (!t) setV(value);
    else if (t !== value) onSave(t);
  };
  return (
    <input className="cell-input wide" value={v} maxLength={80} aria-label={label} disabled={disabled}
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setV(value); }} />
  );
}
